import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Waypoint } from 'react-waypoint';
import { injectIntl } from 'react-intl';

import {
  fetchCollections,
  clearCollectionsList,
  clearSelectedCollections,
} from '../actions/Collections';
import { setCollectionUuid } from '../actions/Files';

import CollectionsGrid from '../components/Collections/Grid';
import CollectionsList from '../components/Collections/List';
import CollectionsMoveModal from '../components/Collections/MoveModalV2';
import CollectionsSelectAll from '../components/Collections/SelectAll';
import CollectionsMiniGrid from '../mini/components/Collections/Grid';
import CollectionReorder from '../components/Collections/Reorder';
import Sort from '../components/Filters/Sort';
import ViewMode from '../components/Filters/ViewMode';

import {
  isFavorites,
  isMini,
  isTrash,
} from '../helpers/App';
import { getCollectionAllowedViews, getCollectionsView } from '../helpers/Collections';

import Loading from '../components/Loading';

class CollectionsSection extends Component {
  constructor(props) {
    super(props);

    this.state = {
      collectionsExpanded: true,
    };

    this.loadCollections = this.loadCollections.bind(this);
    this.clearCollections = this.clearCollections.bind(this);
    this.switchCollections = this.switchCollections.bind(this);
  }

  componentWillUnmount() {
    const { dispatch } = this.props;
    dispatch(clearSelectedCollections());
  }

  getLoadedCollectionsLabel() {
    const {
      collections,
      collectionsTotal,
    } = this.props;

    if (collectionsTotal === 0) {
      return null;
    }

    return `(${collections.length} of ${collectionsTotal})`;
  }

  isAllCollectionsPage() {
    const { match } = this.props;
    return match.path === '/collections/all' || match.path === '/archive/collections/all';
  }

  isAllCollections() {
    const { collection } = this.props;
    return this.isAllCollectionsPage() && !collection;
  }

  isMyCollectionsPage() {
    const { match } = this.props;
    return match.path === '/collections/my' || match.path === '/archive/collections/my';
  }

  isMyCollections() {
    const { collection } = this.props;
    return this.isMyCollectionsPage() && collection.my;
  }

  switchCollections(e) {
    e.preventDefault();
    this.setState(prevState => ({ collectionsExpanded: !prevState.collectionsExpanded }));
  }

  loadCollections() {
    const {
      dispatch,
      collection,
    } = this.props;
    if (
      collection ||
      this.isAllCollections() ||
      this.isMyCollections() ||
      isFavorites() ||
      isTrash()
    ) {
      dispatch(fetchCollections({ my: this.isMyCollections() }));
    }
  }

  clearCollections(reload = false) {
    const { dispatch } = this.props;

    dispatch(clearCollectionsList());
    dispatch(clearSelectedCollections());
    dispatch(setCollectionUuid(null));
    if (reload) {
      dispatch(fetchCollections());
    }
    this.setState({ collectionsExpanded: false });
  }

  render() {
    const {
      intl,
      collection,
      collections,
      collectionsEnd,
      handleMoveSelectedItems,
      collectionsLoading,
      initialCollectionsLoading,
      selectedCollections,
      collectionMoveModal,
      moveModalCollection,
      setOrder,
      setCollectionsView,
      filesParams,
      search,
    } = this.props;
    const { collectionsExpanded } = this.state;
    const {
      orderby,
      order,
    } = filesParams;
    const collectionsView = getCollectionsView();

    let moveModalCollections = [];
    if (selectedCollections.length > 0) {
      moveModalCollections = selectedCollections;
    } else if (moveModalCollection) {
      moveModalCollections = [moveModalCollection];
    }

    if (collections.length === 0) {
      if (initialCollectionsLoading) {
        return (
          <Loading />
        );
      }

      if (!isFavorites() && !isTrash()) {
        return null;
      }
    }

    return (
      <>
        {!isMini() && (
          <div className="collection-tools">
            <div className="list-switch">
              <a
                href="#collections-switch"
                onClick={this.switchCollections}
              >
                {intl.formatMessage({
                  defaultMessage: 'Collections {label}',
                }, { label: this.getLoadedCollectionsLabel() })}{' '}
                {collectionsExpanded ? (
                  <i className="far fa-chevron-down" />
                ) : (
                  <i className="far fa-chevron-right" />
                )}
              </a>
            </div>
            {collectionsExpanded && (
              <div className="collection-tools-actions">
                <div className="collection-tools-actions-buttons">
                  <Sort
                    orderby={orderby}
                    order={order}
                    search={search}
                    setOrder={setOrder}
                    collection={collection}
                    size="sm"
                  />
                  <ViewMode
                    view={collectionsView}
                    setView={setCollectionsView}
                    allowedViews={getCollectionAllowedViews()}
                    size="sm"
                  />
                  <CollectionReorder size="sm" />
                </div>
                <CollectionsSelectAll />
              </div>
            )}
          </div>
        )}
        {collectionsExpanded && (
          isMini() ? (
            <CollectionsMiniGrid
              collections={collections}
            />
          ) : (
            <>
              {collectionsView === 'list' ? (
                <CollectionsList
                  collections={collections}
                  handleMoveSelectedItems={handleMoveSelectedItems}
                />
              ) : (
                <CollectionsGrid
                  collections={collections}
                  handleMoveSelectedItems={handleMoveSelectedItems}
                  selectedView={collectionsView}
                />
              )}
              {collectionsLoading && (
                <Loading />
              )}
              {!collectionsEnd && (
                <Waypoint
                  bottomOffset="-200px"
                  onEnter={this.loadCollections}
                />
              )}
            </>
          )
        )}
        {collectionMoveModal && (
          <CollectionsMoveModal collections={moveModalCollections} />
        )}
      </>
    );
  }
}

const mapStateToProps = state => ({
  collection: state.Collections.collection,
  collections: state.Collections.collections,
  collectionsTotal: state.Collections.collectionsTotal,
  collectionsLoading: state.Collections.collectionsLoading,
  collectionsEnd: state.Collections.collectionsEnd,
  selectedCollections: state.Collections.selectedCollections,
  filters: state.Collections.filters,
  search: state.Search.search,
  collectionMoveModal: state.Collections.moveModalOpen,
  moveModalCollection: state.Collections.moveModalCollection,
  filesParams: state.Files.params,
});

CollectionsSection.propTypes = {
  intl: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  match: PropTypes.object.isRequired,
  collection: PropTypes.object,
  collections: PropTypes.array.isRequired,
  collectionsTotal: PropTypes.number.isRequired,
  collectionsLoading: PropTypes.bool.isRequired,
  collectionsEnd: PropTypes.bool.isRequired,
  selectedCollections: PropTypes.array.isRequired,
  handleMoveSelectedItems: PropTypes.func.isRequired,
  initialCollectionsLoading: PropTypes.bool.isRequired,
  collectionMoveModal: PropTypes.bool.isRequired,
  moveModalCollection: PropTypes.object,
  filesParams: PropTypes.object.isRequired,
  search: PropTypes.string,
  setOrder: PropTypes.func.isRequired,
  setCollectionsView: PropTypes.func.isRequired,
};

CollectionsSection.defaultProps = {
  collection: null,
  moveModalCollection: null,
  search: null,
};

export default withRouter(injectIntl(connect(mapStateToProps)(CollectionsSection)));
